import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { CalendarRange, Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { formatBatchMonth } from '@/lib/format';
import { apiClient, type ApiSuccess } from '@/lib/api-client';
import { useBatchContext } from '@/app/BatchContext';
import { CreateBatchDialog } from '@/features/monthly-batches/CreateBatchDialog';
import { resolvePerBatchPath } from '@/lib/batch-routes';
import { useCurrentUser } from '@/lib/auth-context';
import { hasPermission } from '@/lib/permissions';
import { MONTHLY_BATCH_SELECTOR_OPTIONS_QUERY_KEY } from '@/lib/monthly-batch-query';

interface MonthlyBatchSelectorMonth {
  monthly_batch_id: string;
  year_month: string;
  status: string;
  display_code: string;
}

interface MonthlyBatchSelectorFacility {
  facility_id: string;
  facility_name: string;
  facility_role: string;
  company_display_name: string;
  company_party_type: string;
  label: string;
  months: MonthlyBatchSelectorMonth[];
}

interface MonthlyBatchSelectorOptions {
  facilities: MonthlyBatchSelectorFacility[];
}

const batchScopedPathPattern = /^\/batches\/[0-9a-fA-F-]{36}(\/.*)?$/;
const batchScopedPathWithIdPattern = /^\/batches\/([0-9a-fA-F-]{36})(\/.*)?$/;
const EMPTY_FACILITIES: MonthlyBatchSelectorFacility[] = [];

export const BatchSelector = () => {
  const { t } = useTranslation();
  const { batchId, setBatchId } = useBatchContext();
  const user = useCurrentUser();
  const navigate = useNavigate();
  const location = useLocation();
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedFacilityId, setSelectedFacilityId] = useState<string | null>(null);
  const [emptyFacilitySelectionId, setEmptyFacilitySelectionId] = useState<string | null>(null);
  const pendingNavigationBatchIdRef = useRef<string | null>(null);
  const canCreateBatch = hasPermission(user, 'batch.manage');

  const selectBatch = useCallback(
    (newId: string, options: { replace?: boolean; fallbackToDetail?: boolean } = {}) => {
      setEmptyFacilitySelectionId(null);
      setBatchId(newId);
      const shortcutTarget = resolvePerBatchPath(location.pathname, newId);
      if (shortcutTarget !== location.pathname) {
        pendingNavigationBatchIdRef.current = newId;
        navigate(shortcutTarget, { replace: options.replace });
        return;
      }
      const m = location.pathname.match(batchScopedPathPattern);
      if (m) {
        const rest = m[1] ?? '';
        pendingNavigationBatchIdRef.current = newId;
        navigate(`/batches/${newId}${rest}`, { replace: options.replace });
        return;
      }
      if (options.fallbackToDetail) {
        pendingNavigationBatchIdRef.current = newId;
        navigate(`/batches/${newId}`, { replace: options.replace });
      }
    },
    [location.pathname, navigate, setBatchId],
  );

  const query = useQuery<MonthlyBatchSelectorOptions>({
    queryKey: MONTHLY_BATCH_SELECTOR_OPTIONS_QUERY_KEY,
    queryFn: async () => {
      const r = await apiClient.get<ApiSuccess<MonthlyBatchSelectorOptions>>(
        '/monthly-batches/selector-options',
      );
      return r.data.data;
    },
  });

  const facilities = query.data?.facilities ?? EMPTY_FACILITIES;
  const allMonths = useMemo(
    () =>
      facilities.flatMap((facility) =>
        facility.months.map((month) => ({
          facility,
          month,
        })),
      ),
    [facilities],
  );
  const pathBatchId =
    location.pathname.match(batchScopedPathWithIdPattern)?.[1].toLowerCase() ?? null;
  const pendingNavigationBatchId = pendingNavigationBatchIdRef.current?.toLowerCase() ?? null;
  const shouldApplyPathBatch =
    !emptyFacilitySelectionId &&
    (!pendingNavigationBatchId || pendingNavigationBatchId === pathBatchId);
  const selectedFromPath = shouldApplyPathBatch
    ? (allMonths.find(({ month }) => month.monthly_batch_id.toLowerCase() === pathBatchId) ?? null)
    : null;
  const selectedFromContext =
    allMonths.find(({ month }) => month.monthly_batch_id === batchId) ?? null;
  const selected = selectedFromPath ?? selectedFromContext;
  const currentFacility =
    selected?.facility ??
    facilities.find((facility) => facility.facility_id === selectedFacilityId) ??
    facilities[0] ??
    null;
  const currentMonth =
    currentFacility?.months.find(
      (month) => month.monthly_batch_id === (selected?.month.monthly_batch_id ?? batchId),
    ) ?? null;

  useEffect(() => {
    if (!query.data) return;

    if (batchId && query.isFetching) {
      return;
    }

    if (facilities.length === 0) {
      setSelectedFacilityId(null);
      setEmptyFacilitySelectionId(null);
      if (batchId) setBatchId(null);
      return;
    }

    if (selectedFromPath) {
      setEmptyFacilitySelectionId(null);
      pendingNavigationBatchIdRef.current = null;
      setSelectedFacilityId(selectedFromPath.facility.facility_id);
      if (batchId !== selectedFromPath.month.monthly_batch_id) {
        setBatchId(selectedFromPath.month.monthly_batch_id);
      }
      return;
    }

    if (selectedFromContext) {
      setEmptyFacilitySelectionId(null);
      setSelectedFacilityId(selectedFromContext.facility.facility_id);
      return;
    }

    const defaultFacility =
      facilities.find((facility) => facility.facility_id === selectedFacilityId) ?? facilities[0];
    const defaultMonth = defaultFacility?.months[0];
    if (defaultMonth) {
      setSelectedFacilityId(defaultFacility.facility_id);
      selectBatch(defaultMonth.monthly_batch_id, {
        replace: true,
        fallbackToDetail: !!pathBatchId,
      });
    } else if (defaultFacility) {
      setSelectedFacilityId(defaultFacility.facility_id);
    }
  }, [
    batchId,
    emptyFacilitySelectionId,
    facilities,
    pathBatchId,
    query.data,
    query.isFetching,
    selectBatch,
    selectedFacilityId,
    selectedFromContext,
    selectedFromPath,
    setBatchId,
  ]);

  const handleFacilityChange = (facilityId: string) => {
    setSelectedFacilityId(facilityId);
    const latestMonth = facilities.find((facility) => facility.facility_id === facilityId)
      ?.months[0];
    if (latestMonth) {
      selectBatch(latestMonth.monthly_batch_id, { fallbackToDetail: true });
    } else {
      setEmptyFacilitySelectionId(facilityId);
      setBatchId(null);
      if (location.pathname.match(batchScopedPathPattern)) {
        pendingNavigationBatchIdRef.current = null;
        navigate('/', { replace: true });
      }
    }
  };

  const handleMonthChange = (monthId: string) => {
    selectBatch(monthId, { fallbackToDetail: true });
  };

  const disabled = query.isLoading || query.isError || facilities.length === 0;
  const statusLabel = currentMonth
    ? t(`efs.common.status.${currentMonth.status}`, { defaultValue: currentMonth.status })
    : '';

  return (
    <>
      <div className="flex min-w-0 max-w-full flex-col gap-1 sm:max-w-[34rem] sm:flex-row sm:items-center">
        <div className="flex min-w-0 items-center gap-1.5 text-xs font-medium text-efs-text-muted sm:sr-only">
          <CalendarRange className="h-4 w-4 shrink-0" />
          <span>{t('efs.common.topnav.batch_selector')}</span>
        </div>

        <div className="min-w-0 sm:w-56">
          <span className="mb-0.5 block truncate text-[10px] font-medium leading-none text-efs-text-muted">
            {t('efs.common.topnav.facility_selector')}
          </span>
          <Select
            value={currentFacility?.facility_id ?? ''}
            onValueChange={handleFacilityChange}
            disabled={disabled}
          >
            <SelectTrigger
              className="h-8 w-full min-w-0"
              aria-label={t('efs.common.topnav.facility_selector')}
            >
              <SelectValue
                placeholder={
                  query.isLoading
                    ? t('efs.common.messages.loading')
                    : query.isError
                      ? t('efs.common.messages.batch_selector_error')
                      : t('efs.common.messages.no_batch_selected')
                }
              />
            </SelectTrigger>
            <SelectContent className="max-w-[calc(100vw-2rem)] sm:w-72">
              {facilities.map((facility) => (
                <SelectItem key={facility.facility_id} value={facility.facility_id}>
                  <span className="block truncate">{facility.label}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="min-w-0 sm:w-40">
          <span className="mb-0.5 block truncate text-[10px] font-medium leading-none text-efs-text-muted">
            {t('efs.common.topnav.month_selector')}
          </span>
          <Select
            value={currentMonth?.monthly_batch_id ?? ''}
            onValueChange={handleMonthChange}
            disabled={disabled || !currentFacility || currentFacility.months.length === 0}
          >
            <SelectTrigger
              className="h-8 w-full min-w-0"
              aria-label={t('efs.common.topnav.month_selector')}
            >
              <SelectValue
                placeholder={
                  currentFacility && currentFacility.months.length === 0
                    ? t('efs.common.messages.empty')
                    : t('efs.common.topnav.month_selector')
                }
              />
            </SelectTrigger>
            <SelectContent className="max-w-[calc(100vw-2rem)] sm:w-56">
              {currentFacility?.months.map((month) => (
                <SelectItem key={month.monthly_batch_id} value={month.monthly_batch_id}>
                  <span className="block truncate">
                    {formatBatchMonth(month.year_month)} · {month.display_code}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {currentMonth ? (
          <span className="hidden max-w-24 truncate text-xs text-efs-text-muted lg:inline">
            {statusLabel}
          </span>
        ) : null}

        {canCreateBatch ? (
          <Button
            variant="ghost"
            size="sm"
            className="h-8 shrink-0 justify-start gap-1.5 px-2"
            onClick={() => setCreateOpen(true)}
            title={t('efs.batches.create.button', {
              ns: 'batches',
              defaultValue: '月合計表を作成',
            })}
          >
            <Plus className="h-4 w-4" />
            <span className="hidden md:inline">
              {t('efs.batches.create.button', {
                ns: 'batches',
                defaultValue: '月合計表を作成',
              })}
            </span>
          </Button>
        ) : null}
      </div>
      {canCreateBatch ? <CreateBatchDialog open={createOpen} onOpenChange={setCreateOpen} /> : null}
    </>
  );
};
